import React, {useEffect, useRef, useState} from 'react';
import {Navigate, useLocation} from 'react-router-dom';

import {ObLoading} from './ObLoading';
import {useFicha21} from '../../context/UserModulesContext';

/**
 * La única puerta de entrada al túnel: decide si la agencia hace el recorrido nuevo o el de siempre.
 *
 * Existe como pantalla propia —y no como una condición dentro del registro— porque la agencia ACABA
 * de crearse: el payload de módulos que hay en memoria se pidió cuando el usuario todavía no tenía
 * agencia, y el proveedor no lo vuelve a pedir solo (sólo reacciona a un cambio de usuario, y acá el
 * usuario es el mismo).
 *
 * **Hay que esperar a que llegue el payload NUEVO, no a que haya alguno.** `loaded` ya viene en
 * `true` con el viejo, así que decidir con eso manda al camino clásico a toda agencia recién
 * registrada — es decir, el túnel no arrancaría nunca por la puerta que se construyó para eso. Por
 * eso se espera a ver el ciclo completo: el pedido baja `loaded` a `false` y lo vuelve a subir
 * cuando la respuesta llegó.
 */

// Si la respuesta no llega, se decide igual con lo que haya: dejar al usuario mirando un spinner
// después de crear su agencia es peor que mandarlo al camino de siempre.
//
// «Lo que haya» es el último payload conocido: mientras `loaded` está en `false`, el provider deja
// `ficha21` con el valor que traía del cache. Por eso el render exige `decided` y NADA MÁS —
// pedirle además `loaded` anulaba este escape: un pedido colgado nunca lo vuelve a subir y el
// usuario se quedaba con el spinner para siempre, que es exactamente lo que este tope evita.
const MAX_WAIT_MS = 8000;

export function OnboardingGate() {
    const {ficha21, loaded, refresh} = useFicha21();
    const location = useLocation();
    const asked = useRef(false);
    const sawReload = useRef(false);
    const [decided, setDecided] = useState(false);

    useEffect(() => {
        if (asked.current) return;
        asked.current = true;
        refresh();
        const giveUp = setTimeout(() => setDecided(true), MAX_WAIT_MS);
        return () => clearTimeout(giveUp);
    }, [refresh]);

    useEffect(() => {
        if (!asked.current) return;
        if (!loaded) {
            sawReload.current = true;
            return;
        }
        // Volvió a estar cargado DESPUÉS de haberlo pedido: esto ya es el payload nuevo.
        if (sawReload.current) setDecided(true);
    }, [loaded]);

    // Esta espera puede durar los ocho segundos del tope y es el PRIMER momento del producto: el
    // usuario acaba de crear su agencia. Un spinner sin una palabra ahí es la peor bienvenida
    // posible, y además es indistinguible de una pantalla colgada.
    if (!decided) return <ObLoading titleKey="onboarding.loading.gate" standalone/>;

    if (ficha21) return <Navigate to="/onboarding/plataformas" replace/>;

    // El camino de siempre, con el estado que traía: la agencia que no entra a la experiencia nueva
    // aterriza exactamente donde aterrizaba antes de que el túnel existiera.
    return <Navigate to="/agency/conections" replace state={location.state || {showOnBoarding: true}}/>;
}

/**
 * El guardián de los pasos: sin la experiencia nueva no hay túnel.
 *
 * Se usa en cada paso en vez de una sola vez al entrar porque a los pasos se llega también por
 * enlace directo — desde el drawer de primeros pasos, o de vuelta de autorizar una plataforma.
 */
export function OnboardingGuard({children}) {
    const {ficha21, loaded} = useFicha21();

    // Es la espera que se ve al VOLVER de autorizar una plataforma: el proveedor devuelve al paso 2
    // con una navegación de página entera, así que el usuario aterriza acá antes que en el paso.
    if (!loaded) return <ObLoading titleKey="onboarding.loading.step" standalone/>;
    if (!ficha21) return <Navigate to="/agency/conections" replace/>;
    return children;
}
